export type columnID =
  | 'roid'
  | 'date'
  | 'hour'
  | 'groupInter'
  | 'media'
  | 'sendStatus'
  | 'concession'
  | 'pkInit'
  | 'pkEnd'
  | 'description'
  | 'road'
  | 'capitol'
  | 'subCapitol'
  | 'predefinedOrder'
  | 'state'
  | 'select'
  | 'name'
  | 'weather'
  | 'mediumKnowledge'
  | 'type'
  | 'registerInitDate'
  | 'creationDate'
  | 'registerEndDate'
  | 'derivedEndDate'
  | 'registerArrivalDate'
  | 'direction'
  | 'externalCompanies'
  | 'track'
  | 'lane'
  | 'activity'
  | 'laneCut'
  | 'materials'
  | 'team'
  | 'user'
  | 'registeredBy'
  | 'operators'
  // Columna "Creado por" del RV (2026-07-17) — id distinto de 'user' (la
  // columna Creador genérica, oculta en RV vía SR_HIDDEN_COLUMN_IDS) porque
  // en RV el dato sale de la Order gemela ya resuelto a nombre
  // (row.creatorName del adapter) y la columna se activa por prop
  // (showCreatorColumn), no por visibilidad.
  | 'creator'
  | 'containerType';

export type cellProps = {
  hiddenColumns: number;
  isFigure?: boolean;
  column: columnID;
};

export type trProps = {
  checked: boolean;
  indeterminate?: boolean;
};

type columnIDTypes = { [key: string]: { label: string; id: columnID } };

// Helper function para evitar problemas de tipos con @tanstack/react-table
export const getColumnId = (id: columnID): any => id;

// Function to get column names with translations
export const getColumnNames = (t: (key: string) => string): columnIDTypes => ({
  state: {
    label: t('common:tables.headers.state'),
    id: 'state',
  },
  roid: {
    label: t('common:tables.headers.roid'),
    id: 'roid', // Keep 'roid' as column ID for GraphQL compatibility
  },
  type: {
    label: t('common:tables.headers.type'),
    id: 'type',
  },
  registerInitDate: {
    label: t('common:tables.headers.registerInitDate'),
    id: 'registerInitDate',
  },
  creationDate: {
    label: t('common:tables.headers.creationDate'),
    id: 'creationDate',
  },
  // Fechas de la actuación (solo se usan en la tabla de actuaciones / ValidationsTable).
  // Inicio reusa la columna `registerInitDate` (renombrada inline a "Fecha de inicio" allí).
  registerEndDate: {
    label: t('forms:workOrder.endDate'),
    id: 'registerEndDate',
  },
  derivedEndDate: {
    label: t('forms:workOrder.endDate'),
    id: 'derivedEndDate',
  },
  registerArrivalDate: {
    label: t('forms:workOrder.arrivalDate'),
    id: 'registerArrivalDate',
  },
  name: {
    label: t('common:tables.headers.name'),
    id: 'name',
  },
  capitol: {
    label: t('common:tables.headers.capitol'),
    id: 'capitol',
  },
  subCapitol: {
    label: t('common:tables.headers.subCapitol'),
    id: 'subCapitol',
  },
  predefinedOrder: {
    label: t('forms:workOrder.predefined'),
    id: 'predefinedOrder',
  },
  groupInter: {
    label: t('common:tables.headers.groupInter'),
    id: 'groupInter',
  },
  media: {
    label: t('common:tables.headers.media'),
    id: 'media',
  },
  sendStatus: {
    label: t('common:tables.headers.sendStatus'),
    id: 'sendStatus',
  },
  mediumKnowledge: {
    label: t('common:tables.headers.mediumKnowledge'),
    id: 'mediumKnowledge',
  },
  weather: {
    label: t('common:tables.headers.weather'),
    id: 'weather',
  },
  concession: {
    label: t('common:tables.headers.concession'),
    id: 'concession',
  },
  road: {
    label: t('common:tables.headers.road'),
    id: 'road',
  },
  track: {
    label: t('forms:workOrder.roadType'),
    id: 'track',
  },
  // Carril: la clave `tables:headers.lane` ya existía en ca/es/en (la usan las
  // tablas de gestión), así que la columna no estrena ninguna clave i18n. El
  // valor lo traduce el selector con `laneLabel`, igual que `track`.
  lane: {
    label: t('tables:headers.lane'),
    id: 'lane',
  },
  activity: {
    label: t('forms:workOrder.action'),
    id: 'activity',
  },
  laneCut: {
    label: t('common:tables.headers.laneCut'),
    id: 'laneCut',
  },
  materials: {
    // Reusa la clave que ya existe en los 3 idiomas (Materials/Materiales/Materials).
    label: t('workOrders:materials'),
    id: 'materials',
  },
  pkInit: {
    label: t('common:tables.headers.pkInit'),
    id: 'pkInit',
  },
  pkEnd: {
    label: t('common:tables.headers.pkEnd'),
    id: 'pkEnd',
  },
  description: {
    label: t('common:tables.headers.description'),
    id: 'description',
  },
  direction: {
    label: t('common:workOrder.direction'),
    id: 'direction',
  },
  externalCompanies: {
    label: t('common:tables.headers.externalCompanies'),
    id: 'externalCompanies',
  },
  team: {
    label: t('common:tables.headers.team'),
    id: 'team',
  },
  user: {
    label: t('common:labels.creator'),
    id: 'user',
  },
  // "Registrado por" (Order.registeredByUserId ya resuelto a nombre en los
  // adapters de fila): la PERSONA real que rellena los datos, que en tablets
  // compartidas no es la cuenta con la que se ha entrado.
  //
  // La columna nació (2026-07-23) reutilizando `tables:headers.operator` para
  // no crear claves nuevas, y le quedó el label "Operario" — que choca con la
  // columna "Operarios" (la cuadrilla) y con "Creador" (la cuenta que creó el
  // registro). Las claves propias existen desde 2026-07-27 y ya se usan en el
  // formulario; aquí faltaba el mismo rename.
  registeredBy: {
    label: t('forms:registeredBy.label'),
    id: 'registeredBy',
  },
  operators: {
    label: t('common:tabs.operators'),
    id: 'operators',
  },
  // Fase 4 nav Trabajo/Revisión (2026-07-12) — columna "Origen" (containerType
  // de la fila: generic|campaign|surveillanceRoute) en las 4 tablas GLOBALES
  // de entidad. Key i18n nueva aprobada por el usuario (CA/ES "Origen", EN
  // "Origin") — ver public/locales/{ca,es,en}/common.json → tables.headers.origin.
  // id `containerType` (no `origin`): el accessor key de TanStack DEBE
  // coincidir con el nombre real del campo en la fila (servido directo por
  // el backend — ver gql/fragments/{accident,action,communications,orders}.ts)
  // — el label i18n es independiente del id.
  containerType: {
    label: t('common:tables.headers.origin'),
    id: 'containerType',
  },
});

// For backward compatibility - use default Catalan values
export const columnNames: columnIDTypes = {
  state: {
    label: 'Estat',
    id: 'state',
  },
  roid: {
    label: 'ROID',
    id: 'roid',
  },
  type: {
    label: 'Tipus',
    id: 'type',
  },
  registerInitDate: {
    label: 'Data de registre',
    id: 'registerInitDate',
  },
  creationDate: {
    label: 'Data del sistema',
    id: 'creationDate',
  },
  registerEndDate: {
    label: 'Data final',
    id: 'registerEndDate',
  },
  derivedEndDate: {
    label: 'Data final',
    id: 'derivedEndDate',
  },
  registerArrivalDate: {
    label: "Data d'arribada",
    id: 'registerArrivalDate',
  },
  name: {
    label: 'Nom de la campanya',
    id: 'name',
  },
  capitol: {
    label: 'Capítol',
    id: 'capitol',
  },
  subCapitol: {
    label: 'Subcapítol',
    id: 'subCapitol',
  },
  predefinedOrder: {
    label: 'Predefinits',
    id: 'predefinedOrder',
  },
  groupInter: {
    label: 'Grup Inter.',
    id: 'groupInter',
  },
  media: {
    label: 'Mitjà',
    id: 'media',
  },
  sendStatus: {
    label: 'Env/Reb',
    id: 'sendStatus',
  },
  mediumKnowledge: {
    label: 'Medi',
    id: 'mediumKnowledge',
  },
  weather: {
    label: 'Temps',
    id: 'weather',
  },
  concession: {
    label: 'CON.',
    id: 'concession',
  },
  road: {
    label: 'Crt.',
    id: 'road',
  },
  track: {
    label: 'Tipus vial',
    id: 'track',
  },
  activity: {
    label: 'Acció',
    id: 'activity',
  },
  laneCut: {
    label: 'Tall de carril',
    id: 'laneCut',
  },
  materials: {
    label: 'Materials',
    id: 'materials',
  },
  pkInit: {
    label: 'Pk inicial',
    id: 'pkInit',
  },
  pkEnd: {
    label: 'Pk final',
    id: 'pkEnd',
  },
  description: {
    label: 'Descripció',
    id: 'description',
  },
  direction: {
    label: 'Direcció',
    id: 'direction',
  },
  externalCompanies: {
    label: 'Empresa externa',
    id: 'externalCompanies',
  },
  team: {
    label: 'Equip',
    id: 'team',
  },
  operators: {
    label: 'Operaris',
    id: 'operators',
  },
  containerType: {
    label: 'Origen',
    id: 'containerType',
  },
};
